/**
 * BlogSearch - blog search input wired to the section-rendered search results.
 * Custom element: <blog-search>
 *
 * Refs:
 *   [data-ref="searchForm"]  (required)
 *   [data-ref="searchInput"]  (required)
 *   [data-ref="resultsBar"]  (required)
 *   [data-ref="backButton"]  (required)
 *   [data-ref="resultsCount"]  (required)
 *   [data-ref="resultsLabel"]  (required)
 *   [data-ref="searchTerm"]  (required)
 *
 * Loading: lazy-loaded by the theme loader (theme.js), which imports this
 * file by its exact name - the filename is load-bearing (import map in
 * snippets/theme-head.liquid).
 * Reconstructed to readable identifiers from the original minified vendor
 * bundle; behaviour verified unchanged by AST alpha-equivalence.
 */
var defineProperty = Object.defineProperty;
var setFunctionName = (target, functionName) => defineProperty(target, "name", {
  value: functionName,
  configurable: !0
});
import { ThemeComponent, Blog, dataLayerPush } from "./theme.js";
import "./vendor.js";
class BlogSearch extends ThemeComponent {
  init() {
    const initialQuery = new URLSearchParams(window.location.search).get("q");
    initialQuery && this.searchInput && (this.searchInput.value = initialQuery), this.backButton && this.backButton.addEventListener("click", () => {
      this.clearSearch();
    }), this.observeArticlesContainer(), new URLSearchParams(window.location.search).get("q") && setTimeout(() => {
      this.updateResultsBar();
    }, 200), this.$listen(this.searchForm, "submit", async submitEvent => {
      submitEvent.preventDefault(), submitEvent.stopPropagation();
      const searchTerm = this.searchInput.value.trim();
      return searchTerm ? await this.performSearch(searchTerm) : await this.clearSearch(), !1;
    });
  }
  observeArticlesContainer() {
    const blogMain = document.querySelector('[data-ref="blogMain"]');
    if (!blogMain) {
      setTimeout(() => this.observeArticlesContainer(), 500);
      return;
    }
    const articlesContainer = blogMain.querySelector('[data-ref="articlesContainer"]');
    if (!articlesContainer) {
      setTimeout(() => this.observeArticlesContainer(), 500);
      return;
    }
    new MutationObserver(mutations => {
      mutations.some(mutation => mutation.type === "childList" || mutation.type === "attributes" && mutation.attributeName === "data-total-count") && setTimeout(() => {
        this.updateResultsBar();
      }, 50);
    }).observe(articlesContainer, {
      childList: !0,
      subtree: !0,
      attributes: !0,
      attributeFilter: ["data-total-count"]
    }), window.addEventListener("popstate", () => {
      this.updateResultsBar();
    }), window.addEventListener("hashchange", () => {
      this.updateResultsBar();
    }), window.addEventListener("blog-articles-updated", () => {
      setTimeout(() => {
        this.updateResultsBar();
      }, 50);
    });
  }
  updateResultsBar() {
    if (!this.resultsBar) return;
    const currentQuery = new URLSearchParams(window.location.search).get("q");
    if (!currentQuery || currentQuery.trim() === "") {
      this.resultsBar.classList.add("visibility-hidden"), this.resultsBar.style.display = "none";
      return;
    }
    window.matchMedia("(min-width: 768px)").matches ? (this.resultsBar.classList.remove("visibility-hidden"), this.resultsBar.style.display = "block") : (this.resultsBar.classList.add("visibility-hidden"), this.resultsBar.style.display = "none");
    const blogMain = document.querySelector('[data-ref="blogMain"]'),
      articlesContainer = blogMain == null ? void 0 : blogMain.querySelector('[data-ref="articlesContainer"]'),
      paginationBar = blogMain == null ? void 0 : blogMain.querySelector('[data-ref="paginationBar"]');
    let resultCount = 0,
      totalCountAttr = articlesContainer == null ? void 0 : articlesContainer.getAttribute("data-total-count");
    !totalCountAttr && paginationBar && (totalCountAttr = paginationBar.getAttribute("data-total-count")), totalCountAttr ? resultCount = parseInt(totalCountAttr) || 0 : resultCount = ((articlesContainer == null ? void 0 : articlesContainer.querySelectorAll(".js-blog-main-article-item")) || []).length, this.resultsCount && (this.resultsCount.textContent = resultCount.toString()), this.resultsLabel && (this.resultsLabel.textContent = resultCount === 1 ? "Article Found" : "Articles Found"), this.searchTerm && (this.searchTerm.textContent = currentQuery || "");
  }
  async performSearch(searchTerm) {
    const blogMain = document.querySelector('[data-ref="blogMain"]');
    if (!blogMain) {
      console.error("BlogMain component not found");
      return;
    }
    const blogHandle = blogMain.getAttribute("blog-handle") || "blog",
      isoCode = blogMain.getAttribute("isocode") || "en";
    try {
      const renderedSections = await Blog.render(["blog-main"], blogHandle, isoCode, searchTerm),
        parsedContainer = document.createElement("div");
      parsedContainer.innerHTML = renderedSections["blog-main"];
      const newArticlesContainer = parsedContainer.querySelector('[data-ref="articlesContainer"]'),
        existingArticlesContainer = blogMain.querySelector('[data-ref="articlesContainer"]');
      newArticlesContainer && existingArticlesContainer && (existingArticlesContainer.outerHTML = newArticlesContainer.outerHTML);
      const newPaginationBar = parsedContainer.querySelector('[data-ref="paginationBar"]'),
        existingPaginationBar = blogMain.querySelector('[data-ref="paginationBar"]');
      newPaginationBar && existingPaginationBar ? existingPaginationBar.outerHTML = newPaginationBar.outerHTML : newPaginationBar && !existingPaginationBar ? blogMain.appendChild(newPaginationBar) : !newPaginationBar && existingPaginationBar && existingPaginationBar.remove();
      const searchUrl = new URL(window.location.href);
      searchUrl.searchParams.set("q", searchTerm), searchUrl.searchParams.has("page") && searchUrl.searchParams.delete("page"), window.history.pushState({}, "", searchUrl.toString()), dataLayerPush({
        event: "blog_search",
        search_term: searchTerm,
        blog_handle: blogHandle
      }), setTimeout(() => {
        this.updateResultsBar();
      }, 0), blogMain.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    } catch (searchError) {
      console.error("Error performing search:", searchError);
    }
  }
  async clearSearch() {
    this.searchInput && (this.searchInput.value = "");
    const floatingSearch = document.querySelector('[data-ref="blogSearchFloating"]');
    if (floatingSearch) {
      const floatingInput = floatingSearch.querySelector('[data-ref="searchInput"]');
      floatingInput && (floatingInput.value = "");
    }
    const blogMain = document.querySelector('[data-ref="blogMain"]');
    if (!blogMain) {
      console.error("BlogMain component not found");
      return;
    }
    const blogHandle = blogMain.getAttribute("blog-handle") || "blog",
      isoCode = blogMain.getAttribute("isocode") || "en";
    try {
      const renderedSections = await Blog.render(["blog-main"], blogHandle, isoCode),
        parsedContainer = document.createElement("div");
      parsedContainer.innerHTML = renderedSections["blog-main"];
      const newArticlesContainer = parsedContainer.querySelector('[data-ref="articlesContainer"]'),
        existingArticlesContainer = blogMain.querySelector('[data-ref="articlesContainer"]');
      newArticlesContainer && existingArticlesContainer && (existingArticlesContainer.outerHTML = newArticlesContainer.outerHTML);
      const newPaginationBar = parsedContainer.querySelector('[data-ref="paginationBar"]'),
        existingPaginationBar = blogMain.querySelector('[data-ref="paginationBar"]');
      newPaginationBar && existingPaginationBar ? existingPaginationBar.outerHTML = newPaginationBar.outerHTML : newPaginationBar && !existingPaginationBar ? blogMain.appendChild(newPaginationBar) : !newPaginationBar && existingPaginationBar && existingPaginationBar.remove();
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete("q"), cleanUrl.searchParams.delete("page"), window.history.pushState({}, "", cleanUrl.toString()), this.updateResultsBar();
    } catch (clearError) {
      console.error("Error clearing search:", clearError);
    }
  }
}
setFunctionName(BlogSearch, "BlogSearch");
const propertyTypes = {};
BlogSearch.prototype.$name = "BlogSearch";
BlogSearch.prototype.$info = JSON.parse('{"name":"BlogSearch","meta":{"isLegacy":false,"selector":"blog-search"},"props":[],"refs":[{"selector":"[data-ref=\\"searchForm\\"]","required":true,"propertyKey":"searchForm","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLFormElement"},{"selector":"[data-ref=\\"searchInput\\"]","required":true,"propertyKey":"searchInput","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLInputElement"},{"selector":"[data-ref=\\"resultsBar\\"]","required":true,"propertyKey":"resultsBar","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLElement"},{"selector":"[data-ref=\\"backButton\\"]","required":true,"propertyKey":"backButton","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLButtonElement"},{"selector":"[data-ref=\\"resultsCount\\"]","required":true,"propertyKey":"resultsCount","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLElement"},{"selector":"[data-ref=\\"resultsLabel\\"]","required":true,"propertyKey":"resultsLabel","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLElement"},{"selector":"[data-ref=\\"searchTerm\\"]","required":true,"propertyKey":"searchTerm","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLElement"}]}');
BlogSearch.prototype.$info.component = BlogSearch;
for (const prop of BlogSearch.prototype.$info.props) prop.propertyType = propertyTypes[prop.propertyType];
window.componentService.registerComponent("Blog/BlogSearch.ts", {
  BlogSearch: BlogSearch
}, void 0);